/**
 * Stripe.js, loaded on demand from js.stripe.com (the only way card numbers may be collected for
 * Stripe: they go from Stripe's own iframes to Stripe, never through CortexGrip).
 *
 * Cards are collected with Stripe's Payment Element: one form Stripe draws and validates (card
 * number, expiry, security code, country and postcode, plus Google Pay where the device and the
 * Stripe account allow), mounted inside our page and styled to match it. Apple Pay and Google Pay have
 * tabs of their own, drawn by Stripe's Express Checkout Element (the wallet's button and its payment
 * sheet): saving a card there (`useWalletSetup`), or paying an upgrade in the wallet's sheet
 * (`useWalletPayment`).
 */

export interface StripeError {
  type?: string;
  code?: string;
  message?: string;
}

export interface StripePaymentElement {
  mount(node: HTMLElement): void;
  unmount(): void;
  destroy(): void;
  update(options: Record<string, unknown>): void;
  /** Moves focus into the element (its first field, or the one it marked invalid). */
  focus(): void;
  on(event: "change", handler: (e: { complete: boolean; empty: boolean }) => void): void;
  on(event: "ready", handler: () => void): void;
  on(event: "loaderror", handler: (e: { error: StripeError }) => void): void;
}

/** Which wallet buttons Stripe could draw (undefined: none at all). */
export interface AvailableWallets {
  applePay?: boolean;
  googlePay?: boolean;
}

/** Stripe's Express Checkout Element: the Apple Pay / Google Pay buttons and their payment sheets. */
export interface StripeExpressCheckoutElement {
  mount(node: HTMLElement): void;
  destroy(): void;
  on(event: "ready", handler: (e: { availablePaymentMethods?: AvailableWallets }) => void): void;
  /** Which buttons show, once Stripe knows (`paymentMethods` undefined: none). */
  on(
    event: "availablepaymentmethodschange",
    handler: (e: { paymentMethods?: AvailableWallets }) => void,
  ): void;
  on(event: "loaderror", handler: (e: { error: StripeError }) => void): void;
  on(event: "cancel", handler: () => void): void;
  on(
    event: "confirm",
    handler: (e: {
      expressPaymentType: string;
      /** Closes the payment sheet with a failure, for errors before the confirm call reaches Stripe. */
      paymentFailed: (options?: { reason?: "fail" }) => void;
    }) => void,
  ): void;
}

export interface StripeElements {
  create(type: "payment", options?: Record<string, unknown>): StripePaymentElement;
  create(type: "expressCheckout", options?: Record<string, unknown>): StripeExpressCheckoutElement;
  /** Validates the form (Stripe marks the fields itself) — required before confirming. */
  submit(): Promise<{ error?: StripeError }>;
}

export interface StripeJs {
  elements(options: Record<string, unknown>): StripeElements;
  confirmSetup(options: {
    elements: StripeElements;
    clientSecret: string;
    redirect: "if_required";
    confirmParams: { return_url: string; payment_method_data?: Record<string, unknown> };
  }): Promise<{
    setupIntent?: { status: string; payment_method: string | { id: string } };
    error?: StripeError;
  }>;
  confirmPayment(options: {
    elements: StripeElements;
    clientSecret: string;
    redirect: "if_required";
    confirmParams: { return_url: string };
  }): Promise<{ paymentIntent?: { id: string; status: string }; error?: StripeError }>;
  /** Takes the customer to their bank to approve a payment (3-D Secure) for a card already on file. */
  confirmCardPayment(
    clientSecret: string,
    data?: { payment_method?: string },
  ): Promise<{ paymentIntent?: { id: string; status: string }; error?: StripeError }>;
}

declare global {
  interface Window {
    Stripe?: (publishableKey: string) => StripeJs;
    /** Safari's Apple Pay API (iPhone, iPad, Mac); absent in other browsers. */
    ApplePaySession?: { canMakePayments(): boolean };
  }
}

/**
 * Whether this browser can show Apple Pay at all (Safari on an Apple device, over HTTPS). It says
 * nothing about the Stripe account: the Apple Pay button itself reports that once it has loaded.
 */
export function supportsApplePay(): boolean {
  try {
    return window.ApplePaySession?.canMakePayments() === true;
  } catch {
    return false; // Safari throws on a page that isn't served over HTTPS
  }
}

/**
 * Whether Google Pay could show here: it needs an HTTPS page. Which browsers and accounts have it
 * (Chrome, Edge, Android, with a card saved to Google Pay) the Google Pay button reports once loaded.
 */
export function supportsGooglePay(): boolean {
  return window.isSecureContext !== false;
}

const SCRIPT = "https://js.stripe.com/v3/";
let loading: Promise<void> | null = null;
const instances = new Map<string, StripeJs>();

function loadScript(): Promise<void> {
  if (window.Stripe) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const tag = document.createElement("script");
    tag.src = SCRIPT;
    tag.async = true;
    tag.onload = () => resolve();
    tag.onerror = () => {
      loading = null;
      reject(new Error("Couldn't load the secure card form. Check your connection and try again."));
    };
    document.head.appendChild(tag);
  });
  return loading;
}

export async function loadStripe(publishableKey: string): Promise<StripeJs> {
  const known = instances.get(publishableKey);
  if (known) return known;
  await loadScript();
  if (!window.Stripe) throw new Error("Couldn't load the secure card form.");
  const stripe = window.Stripe(publishableKey);
  instances.set(publishableKey, stripe);
  return stripe;
}

/**
 * Elements options for saving a card for later charges ("deferred" setup: the SetupIntent is only
 * created when the person presses save). `paymentMethodTypes` must match the backend's SetupIntent.
 */
export const SETUP_OPTIONS = {
  mode: "setup",
  paymentMethodTypes: ["card"],
  locale: "en-GB",
  fonts: [{ cssSrc: "https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600&display=swap" }],
  appearance: {
    theme: "stripe",
    labels: "above",
    variables: {
      fontFamily: '"Figtree", ui-sans-serif, system-ui, sans-serif',
      fontSizeBase: "14px",
      colorPrimary: "#f24e29",
      colorText: "#221c19",
      colorTextSecondary: "#5c5551",
      colorTextPlaceholder: "#b3a8a3",
      colorDanger: "#b8291f",
      colorBackground: "#ffffff",
      borderRadius: "10px",
      spacingUnit: "3.5px",
    },
    rules: {
      ".Label": { fontSize: "12px", fontWeight: "500", color: "#5c5551" },
      ".Input": { border: "1px solid #d4cac4", boxShadow: "none", padding: "12px 14px" },
      ".Input:focus": { borderColor: "#f24e29", boxShadow: "0 0 0 2px rgba(242, 78, 41, 0.2)" },
      ".Input--invalid": { borderColor: "#d6342f", boxShadow: "none" },
      ".Tab": { border: "1px solid #d4cac4", boxShadow: "none" },
      ".Tab--selected": { borderColor: "#f24e29", boxShadow: "0 0 0 1px #f24e29" },
    },
  },
} as const;

/**
 * Elements options for a payment approved in a wallet's sheet, which shows `amountCents` (pence,
 * VAT included). Only authorised there, and kept for renewals: both must match the backend's
 * PaymentIntent (`capture_method=manual`, `setup_future_usage=off_session`).
 */
export function paymentOptions(amountCents: number) {
  return {
    ...SETUP_OPTIONS,
    mode: "payment",
    amount: amountCents,
    currency: "gbp",
    captureMethod: "manual",
    setupFutureUsage: "off_session",
  } as const;
}

/**
 * Payment Element options. Stripe shows its own wording that the card is saved for future charges;
 * it is hidden (`terms.card: "never"`) while the person has chosen not to save the card. Apple Pay
 * has its own tab (`walletButtonOptions`), so it never appears inside the card form: a card saved
 * there could otherwise be a one-time card, which Apple Pay's terms rule out. Google Pay shows in the
 * card form at checkout; on Add payment method it has its own tab (`googlePay: false`).
 */
export function paymentElementOptions({ googlePay = true }: { googlePay?: boolean } = {}) {
  return {
    layout: { type: "tabs", defaultCollapsed: false },
    wallets: { applePay: "never", googlePay: googlePay ? "auto" : "never" },
  } as const;
}

export type Wallet = "apple_pay" | "google_pay";

/**
 * The Apple Pay or Google Pay tab: only that wallet's own button, the same height as our buttons.
 * Google Pay is `always`: the person picked its tab, so the button shows in any browser Google Pay
 * supports, and someone not signed in to Google signs in from it (`auto` hides it from them).
 */
export function walletButtonOptions(wallet: Wallet) {
  return {
    paymentMethods: {
      applePay: wallet === "apple_pay" ? "auto" : "never",
      googlePay: wallet === "google_pay" ? "always" : "never",
      link: "never",
      paypal: "never",
      amazonPay: "never",
      klarna: "never",
    },
    buttonType: { applePay: "plain", googlePay: "plain" },
    buttonTheme: { applePay: "black", googlePay: "black" },
    buttonHeight: 44,
    // One wallet per tab, so no overflow menu can appear (Stripe refuses `overflow: "never"` with `maxRows`).
    layout: { maxColumns: 1, maxRows: 1 },
    business: { name: "CortexGrip" },
  } as const;
}
